import { Injectable, inject, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, timer, switchMap, of, EMPTY, catchError } from 'rxjs';
import { BellNotification } from '@model/notification/bell-notification.model';
import { BellNotificationGateway } from '@service/notification/bell-notification.gateway';
import { generateUUID } from '@common/uuid-utils';

const BASE_POLLING_INTERVAL_MS = 30_000;
const MAX_POLLING_INTERVAL_MS = 300_000;
const BROADCAST_CHANNEL_NAME = 'bell-notification-sync';
const LEADER_LEASE_TTL_MS = 90_000;
const MSG_UNREAD_COUNT = 'unread-count';

@Injectable()
export class BellNotificationService implements OnDestroy {
  private readonly gateway = inject(BellNotificationGateway);
  private readonly notificationsSubject = new BehaviorSubject<BellNotification[]>([]);
  private readonly unreadCountSubject = new BehaviorSubject<number>(0);
  private pollingSubscription: Subscription | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private currentInterval = BASE_POLLING_INTERVAL_MS;
  private lastKnownCount = -1;
  private isLeader = false;
  private leadershipTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private leaseCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private lastHeartbeatAt = 0;
  private readonly tabId = generateUUID();

  readonly notifications$: Observable<BellNotification[]> = this.notificationsSubject.asObservable();
  readonly unreadCount$: Observable<number> = this.unreadCountSubject.asObservable();

  constructor() {
    this.initBroadcastChannel();
  }

  startPolling(): void {
    if (this.pollingSubscription || this.leadershipTimeoutId) {
      return;
    }
    this.currentInterval = BASE_POLLING_INTERVAL_MS;
    this.claimLeadership();
  }

  stopPolling(): void {
    this.cancelLeadershipTimeout();
    this.stopLeaseCheck();
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
    if (this.isLeader) {
      this.isLeader = false;
      this.broadcastMessage({ type: 'leader-resign', tabId: this.tabId });
    }
  }

  loadNotifications(): void {
    this.gateway.getUnread().pipe(
      catchError(() => EMPTY)
    ).subscribe((notifications) => {
      this.notificationsSubject.next(notifications);
      const unreadCount = notifications.filter((n) => !n.read).length;
      this.unreadCountSubject.next(unreadCount);
      this.broadcastMessage({ type: MSG_UNREAD_COUNT, count: unreadCount });
    });
  }

  markAsRead(id: string): void {
    this.gateway.markAsRead(id).pipe(
      catchError(() => EMPTY)
    ).subscribe(() => {
      const updated = this.notificationsSubject.value.filter((n) => n.id !== id);
      this.notificationsSubject.next(updated);
      const unreadCount = updated.filter((n) => !n.read).length;
      this.unreadCountSubject.next(unreadCount);
      this.broadcastMessage({ type: MSG_UNREAD_COUNT, count: unreadCount });
    });
  }

  markAllAsRead(): void {
    this.gateway.markAllAsRead().pipe(
      catchError(() => EMPTY)
    ).subscribe(() => {
      this.notificationsSubject.next([]);
      this.unreadCountSubject.next(0);
      this.broadcastMessage({ type: MSG_UNREAD_COUNT, count: 0 });
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopLeaseCheck();
    this.broadcastChannel?.close();
    this.broadcastChannel = null;
  }

  private claimLeadership(): void {
    this.broadcastMessage({ type: 'leader-claim', tabId: this.tabId });
    this.cancelLeadershipTimeout();
    // If no objection arrives within a short window, become leader
    this.leadershipTimeoutId = setTimeout(() => {
      this.leadershipTimeoutId = null;
      if (!this.isLeader) {
        this.isLeader = true;
        this.stopLeaseCheck();
        this.schedulePoll(0);
      }
    }, 200);
  }

  private cancelLeadershipTimeout(): void {
    if (this.leadershipTimeoutId !== null) {
      clearTimeout(this.leadershipTimeoutId);
      this.leadershipTimeoutId = null;
    }
  }

  private schedulePoll(delay: number): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = timer(delay).pipe(
      switchMap(() => this.gateway.getUnreadCount().pipe(catchError(() => of(-1))))
    ).subscribe((count) => {
      if (count >= 0) {
        this.unreadCountSubject.next(count);
        this.broadcastMessage({ type: MSG_UNREAD_COUNT, count });
        this.broadcastMessage({ type: 'leader-heartbeat', tabId: this.tabId });
        this.adjustBackoff(count);
      } else {
        this.currentInterval = Math.min(this.currentInterval * 2, MAX_POLLING_INTERVAL_MS);
      }
      this.schedulePoll(this.currentInterval);
    });
  }

  private adjustBackoff(count: number): void {
    if (count === this.lastKnownCount) {
      this.currentInterval = Math.min(this.currentInterval * 2, MAX_POLLING_INTERVAL_MS);
    } else {
      this.currentInterval = BASE_POLLING_INTERVAL_MS;
    }
    this.lastKnownCount = count;
  }

  private initBroadcastChannel(): void {
    try {
      this.broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      this.broadcastChannel.onmessage = (event: MessageEvent) => {
        this.handleBroadcastMessage(event.data);
      };
    } catch {
      // BroadcastChannel not supported; fall back to independent polling
    }
  }

  private handleBroadcastMessage(data: Record<string, unknown>): void {
    if (!data?.['type']) {
      return;
    }
    const isFromOtherTab = data['tabId'] !== this.tabId;

    switch (data['type']) {
      case MSG_UNREAD_COUNT:
        if (typeof data['count'] === 'number') {
          this.unreadCountSubject.next(data['count']);
          this.lastKnownCount = data['count'];
        }
        break;
      case 'leader-claim':
        if (isFromOtherTab) {
          this.handleLeaderClaim(data);
        }
        break;
      case 'leader-active':
        if (isFromOtherTab && !this.isLeader) {
          this.handleLeaderActive();
        }
        break;
      case 'leader-heartbeat':
        if (isFromOtherTab && !this.isLeader) {
          this.resetLeaseCheck();
        }
        break;
      case 'leader-resign':
        if (isFromOtherTab && !this.isLeader && !document.hidden) {
          this.claimLeadership();
        }
        break;
    }
  }

  private handleLeaderClaim(data: Record<string, unknown>): void {
    if (this.isLeader) {
      this.broadcastMessage({ type: 'leader-active', tabId: this.tabId });
    } else if (this.leadershipTimeoutId && (data['tabId'] as string) < this.tabId) {
      this.cancelLeadershipTimeout();
    }
  }

  private handleLeaderActive(): void {
    this.cancelLeadershipTimeout();
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
    this.resetLeaseCheck();
  }

  private broadcastMessage(message: Record<string, unknown>): void {
    try {
      this.broadcastChannel?.postMessage(message);
    } catch {
      // Ignore broadcast errors
    }
  }

  private resetLeaseCheck(): void {
    this.lastHeartbeatAt = Date.now();
    if (!this.leaseCheckIntervalId) {
      this.leaseCheckIntervalId = setInterval(() => {
        if (Date.now() - this.lastHeartbeatAt > LEADER_LEASE_TTL_MS && !this.isLeader && !document.hidden) {
          this.stopLeaseCheck();
          this.claimLeadership();
        }
      }, LEADER_LEASE_TTL_MS);
    }
  }

  private stopLeaseCheck(): void {
    if (this.leaseCheckIntervalId !== null) {
      clearInterval(this.leaseCheckIntervalId);
      this.leaseCheckIntervalId = null;
    }
  }
}
